import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Plus, Edit, Trash2, Search, User, Mail, Calendar, Shield, Download, Filter, MoreVertical, CheckCircle2, XCircle, Clock, Users, Briefcase, UserCheck, Activity, TrendingUp, ArrowUpRight, Eye, ArrowLeft, LayoutDashboard, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { normalizeMediaUrl } from '../../utils/media';
import Loader from '../ui/Loader';

type TabType = 'overview' | 'jobseekers' | 'employers' | 'activity';

interface UserManagementProps {
  users: any[];
  onUserUpdate: (users: any[]) => void;
}

// Form Fields Component - Moved outside to prevent unmounting on every render
interface UserFormFieldsProps {
  user: any;
  setUser: (user: any) => void;
  isCreateModalOpen: boolean;
  nameInputRef: React.RefObject<HTMLInputElement>;
  emailInputRef: React.RefObject<HTMLInputElement>;
  passwordInputRef: React.RefObject<HTMLInputElement>;
  confirmPasswordInputRef: React.RefObject<HTMLInputElement>;
}

const UserFormFields = ({
  user,
  setUser,
  isCreateModalOpen,
  nameInputRef,
  emailInputRef,
  passwordInputRef,
  confirmPasswordInputRef
}: UserFormFieldsProps) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
    <div className="space-y-2">
      <Label htmlFor="name" className="text-xs">Full Name *</Label>
      <Input
        id="name"
        value={user.name}
        onChange={(e) => setUser({ ...user, name: e.target.value })}
        ref={nameInputRef}
        placeholder="e.g. John Doe"
        className="h-8 text-xs"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="email" className="text-xs">Email Address *</Label>
      <Input
        id="email"
        type="email"
        value={user.email}
        onChange={(e) => setUser({ ...user, email: e.target.value })}
        ref={emailInputRef}
        placeholder="e.g. john@example.com"
        className="h-8 text-xs"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="type" className="text-xs">User Type *</Label>
      <Select value={user.type} onValueChange={(value) => setUser({ ...user, type: value })}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(!isCreateModalOpen || user.type === 'jobseeker') && (
            <SelectItem value="jobseeker">Job Seeker</SelectItem>
          )}
          <SelectItem value="employer">Employer</SelectItem>
          <SelectItem value="super_admin">Super Admin</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {user.type === 'employer' && (
      <div className="space-y-2">
        <Label htmlFor="company" className="text-xs">Company</Label>
        <Input
          id="company"
          value={user.company || ''}
          onChange={(e) => setUser({ ...user, company: e.target.value })}
          placeholder="e.g. Himalayan Bank Limited"
          className="h-8 text-xs"
        />
      </div>
    )}

    {user.type === 'super_admin' && (
      <>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs">Password *</Label>
          <Input
            id="password"
            type="password"
            value={user.password || ''}
            onChange={(e) => setUser({ ...user, password: e.target.value })}
            ref={passwordInputRef}
            placeholder="Enter password (min 8 characters)"
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-xs">Confirm Password *</Label>
          <Input
            id="confirm-password"
            type="password"
            value={user.confirmPassword || ''}
            onChange={(e) => setUser({ ...user, confirmPassword: e.target.value })}
            ref={confirmPasswordInputRef}
            placeholder="Re-enter password"
            className="h-8 text-xs"
          />
        </div>
      </>
    )}

    {user.type === 'jobseeker' && (
      <>
        <div className="md:col-span-2 space-y-2">
          <Label htmlFor="skills" className="text-xs">Skills (comma separated)</Label>
          <Input
            id="skills"
            value={Array.isArray(user.profile?.skills) ? user.profile.skills.join(', ') : user.profile?.skills || ''}
            onChange={(e) => setUser({
              ...user,
              profile: {
                ...user.profile,
                skills: e.target.value
              }
            })}
            placeholder="e.g. JavaScript, React, Node.js"
            className="h-8 text-xs"
          />
        </div>

        <div className="md:col-span-2 space-y-2">
          <Label htmlFor="experience" className="text-xs">Experience</Label>
          <Input
            id="experience"
            value={user.profile?.experience || ''}
            onChange={(e) => setUser({
              ...user,
              profile: {
                ...user.profile,
                experience: e.target.value
              }
            })}
            placeholder="e.g. 3 years in software development"
            className="h-8 text-xs"
          />
        </div>

        <div className="md:col-span-2 space-y-2">
          <Label htmlFor="resume" className="text-xs">Resume/CV Link</Label>
          <Input
            id="resume"
            value={user.profile?.resume || ''}
            onChange={(e) => setUser({
              ...user,
              profile: {
                ...user.profile,
                resume: e.target.value
              }
            })}
            placeholder="e.g. https://example.com/resume.pdf"
            className="h-8 text-xs"
          />
        </div>
      </>
    )}
  </div>
);

export function UserManagement({ users, onUserUpdate }: UserManagementProps) {
  const navigate = useNavigate();

  // Filter & View State
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'jobseeker') return 'jobseekers';
    if (type === 'employer') return 'employers';
    return 'overview';
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [filterType, setFilterType] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('type') || 'all';
  });
  const [filterStatus, setFilterStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('status') || 'all';
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  // Modal & Dialog State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState<{ user: any; resumes: any[] } | null>(null);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    userId: string | null;
    userName: string;
    userType: string;
  }>({
    open: false,
    userId: null,
    userName: '',
    userType: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Data State
  const [localUsers, setLocalUsers] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    jobSeekers: 0,
    employers: 0,
    verified: 0,
    activeToday: 0
  });

  // Refs
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);

  // New User State
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    type: 'employer' as 'jobseeker' | 'employer' | 'super_admin',
    company: '',
    profile: {
      resume: '',
      skills: '',
      experience: ''
    },
    password: '',
    confirmPassword: '',
    isVerified: true
  });

  // Focus effect for create modal
  useEffect(() => {
    if (!isCreateModalOpen) return;
    const id = setTimeout(() => nameInputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isCreateModalOpen]);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const type = params.get('type');
    const search = params.get('search');

    // Always update state based on URL, with fallbacks
    setFilterStatus(status || 'all');
    setFilterType(type || 'all');
    setSearchTerm(search || '');

    // Update active tab based on type
    if (type === 'jobseeker') setActiveTab('jobseekers');
    else if (type === 'employer') setActiveTab('employers');
    else if (!type || type === 'all') setActiveTab('overview');
  }, [location.search]);

  // Debounce search/filter reset
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, filterType, filterStatus]);

  const fetchStats = async () => {
    try {
      const res: any = await apiClient.getAdminDashboardStats();
      setStats({
        totalUsers: res.totalUsers || 0,
        jobSeekers: res.totalJobSeekers || 0,
        employers: res.totalEmployers || 0,
        verified: res.totalVerified || 0,
        activeToday: res.activeUsers || 0
      });
    } catch (e) {
      console.error('Failed to fetch stats', e);
    }
  };


  const fetchUsers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      // Determine type filter
      let typeFilter = undefined;
      if (activeTab === 'jobseekers') typeFilter = 'jobseeker';
      else if (activeTab === 'employers') typeFilter = 'employer';
      else if (activeTab === 'overview' && filterType !== 'all') typeFilter = filterType;

      const res = await apiClient.getAllUsers({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        type: typeFilter,
        status: filterStatus !== 'all' ? filterStatus : undefined
      });

      const fetchedUsers = Array.isArray(res.users) ? res.users : [];

      const mapped = fetchedUsers.map((u: any) => ({
        id: u._id || u.id || String(Date.now()),
        name: u.full_name || u.name || '',
        email: u.email,
        type: (u.user_type === 'job_seeker' ? 'jobseeker' : (u.user_type || '')),
        company: u.company_name || u.company || '',
        profile: u.profile || {},
        isVerified: (u.is_verified ?? u.isVerified ?? true),
        lastActive: u.lastActive || u.updated_at
      }));

      setLocalUsers(mapped);

      if (res.pagination) {
        setTotalPages(res.pagination.total_pages);
        setTotalUsersCount(res.pagination.total_users || res.pagination.total || 0);
        setItemsPerPage(res.pagination.per_page);
      }
    } catch (err) {
      console.error('Failed to load users', err);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, activeTab, filterType, filterStatus, searchTerm]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRefresh = React.useCallback(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers]);

  const onUserActionSuccess = React.useCallback(() => {
    handleRefresh();
  }, [handleRefresh]);

  const fetchUserProfile = async (userId: string) => {
    try {
      const json: any = await apiClient.request(`/admin/users/${userId}/profile`);
      setProfileData({ user: (json as any)?.user ?? json, resumes: Array.isArray((json as any)?.resumes) ? (json as any).resumes : [] });
      setShowProfileDialog(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load user profile');
    }
  };

  const downloadAdminResumePdf = async (resumeId: string) => {
    try {
      const blob = await apiClient.requestBlob(`/admin/resumes/${resumeId}/export/pdf`, { method: 'GET' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Resume.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download PDF');
    }
  };

  const downloadAdminResumeDocx = async (resumeId: string) => {
    try {
      const blob = await apiClient.requestBlob(`/admin/resumes/${resumeId}/export/docx`, { method: 'GET' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Resume.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Word file downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download Word');
    }
  };

  const handleCreateUser = async () => {
    try {
      if (newUser.type === 'super_admin') {
        const pwd = String(newUser.password || '').trim();
        const cpwd = String(newUser.confirmPassword || '').trim();
        if (!pwd || pwd.length < 8) {
          toast.error('Password must be at least 8 characters');
          return;
        }
        if (pwd !== cpwd) {
          toast.error('Passwords do not match');
          return;
        }
        await apiClient.createAdminUser({
          email: String(newUser.email || '').trim(),
          password: pwd,
          full_name: String(newUser.name || '').trim(),
          role: 'super_admin'
        });
        toast.success('Super Admin created successfully');
      } else {
        if (newUser.type === 'employer') {
          await apiClient.createEmployerByAdmin({ user: newUser });
          toast.success('Employer created successfully');
        } else if (newUser.type === 'jobseeker') {
          toast.info('Job Seeker creation not supported via Admin API yet.');
          return;
        }
      }
      setIsCreateModalOpen(false);
      setNewUser({
        name: '',
        email: '',
        type: 'employer',
        company: '',
        profile: { resume: '', skills: '', experience: '' },
        password: '',
        confirmPassword: '',
        isVerified: true
      });
      onUserActionSuccess();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create user');
    }
  };

  const handleEditUser = React.useCallback(async () => {
    if (!selectedUser) return;
    setIsLoading(true);
    try {
      const id = selectedUser.id;
      const t = String(selectedUser.type || '').toLowerCase();
      const staff = t === 'admin' || t === 'super_admin' || t === 'hr';

      // Prepare payload
      const payload: any = {
        full_name: selectedUser.name,
        email: selectedUser.email,
        type: selectedUser.type,
      };

      if (selectedUser.type === 'employer') {
        payload.company = selectedUser.company;
      } else if (selectedUser.type === 'jobseeker') {
        const profileData = { ...selectedUser.profile };
        if (typeof profileData.skills === 'string') {
          profileData.skills = (profileData.skills as string).split(',').map(s => s.trim()).filter(Boolean) as any;
        }
        payload.profile = profileData;
      }

      if (staff) {
        await apiClient.updateAdminUser(id, {
          email: selectedUser.email,
          name: selectedUser.name
        });
      } else {
        await apiClient.updateUserByAdmin(id, payload);
      }

      toast.success('User updated successfully!');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      await onUserActionSuccess();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  }, [selectedUser, onUserActionSuccess]);

  const handleDeleteUser = async () => {
    if (!deleteDialog.userId) return;
    setIsDeleting(true);
    try {
      const id = String(deleteDialog.userId);
      const t = String(deleteDialog.userType || '').toLowerCase();
      const staff = t === 'admin' || t === 'super_admin' || t === 'hr';
      if (staff) {
        await apiClient.deleteAdminUser(id);
      } else {
        await apiClient.request(`/admin/users/${id}/delete`, { method: 'POST' });
      }
      toast.success('User deleted successfully!');
      setDeleteDialog({ open: false, userId: null, userName: '', userType: '' });
      onUserActionSuccess();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'employer':
        return 'bg-blue-100 text-blue-800';
      case 'jobseeker':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  // Stats for the specific requested tabs
  const tabStats = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Users,
      count: stats.totalUsers,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'jobseekers',
      label: 'Job Seekers',
      icon: UserCheck,
      count: stats.jobSeekers,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      id: 'employers',
      label: 'Employers',
      icon: Briefcase,
      count: stats.employers,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'activity',
      label: 'Recent Activity',
      icon: Activity,
      count: stats.activeToday,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const userStats = {
    total: stats.totalUsers,
    jobSeekers: stats.jobSeekers,
    employers: stats.employers,
    verified: stats.verified
  };

  // Main UI
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h2 className="text-2xl font-bold">User Management</h2>
            <p className="text-gray-600">Manage and monitor all platform users</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
              <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto text-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                  <DialogDescription>
                    Add a new user to the platform with their profile information.
                  </DialogDescription>
                </DialogHeader>
                <UserFormFields
                  user={newUser}
                  setUser={setNewUser}
                  isCreateModalOpen={isCreateModalOpen}
                  nameInputRef={nameInputRef}
                  emailInputRef={emailInputRef}
                  passwordInputRef={passwordInputRef}
                  confirmPasswordInputRef={confirmPasswordInputRef}
                />
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateUser}>
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

      <div className="flex justify-center w-full mb-8">
        <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
          {tabStats.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <MenubarMenu key={tab.id} className="flex-1">
                <MenubarTrigger
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`
                    relative z-10 flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 w-full
                    ${isActive ? 'text-white' : 'text-gray-500 hover:text-gray-900'}
                    bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                  `}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeUserTabIndicator"
                      className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                      style={{ zIndex: -1 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </MenubarTrigger>
              </MenubarMenu>
            );
          })}
        </Menubar>
      </div>

        {/* Stats Cards (compact) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-gray-600">Total Users</p>
                  <p className="text-sm font-bold text-gray-900">{userStats.total}</p>
                  <p className="text-[10px] text-green-600 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +12% from last month
                  </p>
                </div>
                <div className="p-1 bg-blue-50 rounded">
                  <Users className="h-3 w-3 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-gray-600">Job Seekers</p>
                  <p className="text-sm font-bold text-gray-900">{userStats.jobSeekers}</p>
                  <p className="text-[10px] text-green-600 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +8% this week
                  </p>
                </div>
                <div className="p-1 bg-green-50 rounded">
                  <UserCheck className="h-3 w-3 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-gray-600">Employers</p>
                  <p className="text-sm font-bold text-gray-900">{userStats.employers}</p>
                  <p className="text-[10px] text-green-600 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +5% this week
                  </p>
                </div>
                <div className="p-1 bg-purple-50 rounded">
                  <Briefcase className="h-3 w-3 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-medium text-gray-600">Verified</p>
                  <p className="text-sm font-bold text-gray-900">{userStats.verified}</p>
                  <p className="text-[10px] text-gray-500">
                    {Math.round((userStats.verified / (userStats.total || 1)) * 100)}% of total
                  </p>
                </div>
                <div className="p-1 bg-orange-50 rounded">
                  <CheckCircle2 className="h-3 w-3 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Advanced Filters */}
        <Card>
          <CardContent className="p-2">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name, email, or company... (ENTER to search)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2 w-full lg:w-auto lg:ml-4 justify-end">
                <Select value={filterType} onValueChange={(val) => {
                  setFilterType(val);
                  const search = new URLSearchParams(location.search);
                  search.set('type', val);
                  navigate({ search: `?${search.toString()}` }, { replace: true });
                }}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9">
                    <SelectValue placeholder="User Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="jobseeker">Job Seekers</SelectItem>
                    <SelectItem value="employer">Employers</SelectItem>
                    <SelectItem value="super_admin">Super Admins</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={(val) => {
                  setFilterStatus(val);
                  const search = new URLSearchParams(location.search);
                  search.set('status', val);
                  navigate({ search: `?${search.toString()}` }, { replace: true });
                }}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="unverified">Unverified</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" className="gap-2 h-9" onClick={() => {
                  setFilterType('all');
                  setFilterStatus('all');
                  setSearchTerm('');
                  navigate({ search: `?section=${new URLSearchParams(location.search).get('section') || 'user-management'}` });
                }}>
                  <Filter className="h-4 w-4" />
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users List with Pagination */}
        <Card>
          <CardHeader className="border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                {activeTab === 'overview' && 'All Users'}
                {activeTab === 'jobseekers' && 'Job Seekers'}
                {activeTab === 'employers' && 'Employers'}
                {activeTab === 'activity' && 'Recent Activity'}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({totalUsersCount} found)
                </span>
              </CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader text="Loading users..." />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-900 text-sm">User</th>
                        <th className="text-left p-3 font-medium text-gray-900 text-sm">Contact</th>
                        <th className="text-left p-3 font-medium text-gray-900 text-sm">Type</th>
                        <th className="text-left p-3 font-medium text-gray-900 text-sm">Status</th>
                        <th className="text-left p-3 font-medium text-gray-900 text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localUsers.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-gray-50 odd:bg-white even:bg-orange-50/40">
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden">
                                {(() => {
                                  const avatarRaw = (user as any).profilePicture || (user as any).profileImage || (user as any).profile_image || (user as any).profile_image_url || (user?.profile as any)?.profilePicture || (user?.profile as any)?.profileImage || (user?.profile as any)?.profile_image || (user?.profile as any)?.profile_image_url || '';
                                  const avatarUrl = normalizeMediaUrl(avatarRaw) || avatarRaw;
                                  return avatarUrl ? (
                                    <ImageWithFallback src={avatarUrl as string} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                                  ) : (
                                    <User className="h-4 w-4 text-white" />
                                  );
                                })()}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                                {user.company && (
                                  <div className="text-xs text-gray-500 truncate">{user.company}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5 text-gray-400" />
                              <span className="text-xs text-gray-700 truncate">{user.email}</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <Badge className={`${getUserTypeColor(user.type)} text-[10px] px-2 py-0.5`}>{user.type === 'jobseeker' ? 'Job Seeker' : user.type === 'employer' ? 'Employer' : (user.type === 'super_admin' ? 'Super Admin' : 'Admin')}</Badge>
                          </td>
                          <td className="py-2.5 px-3">
                            {(user as any)?.isVerified ? (
                              <Badge className="bg-green-100 text-green-800 text-[10px]">verified</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800 text-[10px]">unverified</Badge>
                            )}
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => fetchUserProfile(user.id)}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  // Optimize data transformation
                                  const userCopy = { ...user };
                                  if (userCopy.profile?.skills) {
                                    userCopy.profile = {
                                      ...userCopy.profile,
                                      skills: Array.isArray(userCopy.profile.skills)
                                        ? userCopy.profile.skills.join(', ')
                                        : userCopy.profile.skills
                                    };
                                  }
                                  setSelectedUser(userCopy);
                                  setIsEditModalOpen(true);
                                }}
                              >
                                <Edit className="h-3.5 w-3.5 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-red-600"
                                onClick={() => setDeleteDialog({
                                  open: true,
                                  userId: user.id,
                                  userName: `${user.name || ''}`.trim() || user.email,
                                  userType: user.type || 'user'
                                })}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {localUsers.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                      <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                    </div>
                  )}
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <div className="flex justify-between w-full sm:hidden">
                    <Button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{Math.min(totalUsersCount, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalUsersCount)}</span> of <span className="font-medium">{totalUsersCount}</span> results
                      </p>
                    </div>
                    <div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center px-2">
                          <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit User Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto text-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update the user's profile information and settings.
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <UserFormFields
                user={selectedUser}
                setUser={setSelectedUser}
                isCreateModalOpen={false}
                nameInputRef={nameInputRef}
                emailInputRef={emailInputRef}
                passwordInputRef={passwordInputRef}
                confirmPasswordInputRef={confirmPasswordInputRef}
              />
            )}
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleEditUser} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update User'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Super Admin: View user profile dialog */}
        <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
          <DialogContent className="max-w-3xl" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>User Profile</DialogTitle>
              <DialogDescription>View details and download resumes</DialogDescription>
            </DialogHeader>
            {profileData ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold">{profileData.user.full_name || profileData.user.name || profileData.user.email}</h3>
                  <p className="text-sm text-gray-600">{profileData.user.email}</p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Resumes</h4>
                  {profileData.resumes.length === 0 ? (
                    <p className="text-sm text-gray-500">No resumes found.</p>
                  ) : (
                    <div className="space-y-2">
                      {profileData.resumes.map((r: any) => (
                        <div key={r._id} className="flex items-center justify-between rounded p-2">
                          <div>
                            <div className="font-medium">{r.title || 'Untitled Resume'}</div>
                            <div className="text-xs text-gray-500">Template: {r.template} · Updated: {new Date(r.updatedAt || Date.now()).toLocaleString()}</div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => downloadAdminResumePdf(r._id)}>Download PDF</Button>
                            <Button variant="outline" size="sm" onClick={() => downloadAdminResumeDocx(r._id)}>Download Word</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-8"><Loader text="Loading profile..." /></div>
            )}
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={deleteDialog.open}
          onOpenChange={(open) => { if (!isDeleting) { setDeleteDialog({ open, userId: null, userName: '', userType: '' }); } }}
          onConfirm={handleDeleteUser}
          title="Delete Team Member"
          description={`Are you sure you want to delete this team member? If you delete this member, you will permanently remove their profile and associated data.`}
          confirmText="Delete Member"
          cancelText="Cancel"
          type="danger"
          itemName={deleteDialog.userName}
          isLoading={isDeleting}
          variant="minimal"
        />
      </div>
    </div>
  );
}
